/**
 * The local AssetStore (spec--pdfmarkuptool-image-markup 4.2, slice I2): IndexedDB database `pmt-assets`.
 *
 * Stores:
 * - `assets`: one row per content-addressed asset (sha256 hex), the JPEG Blob beside its meta (spec 3.3).
 * - `uploads`: the upload queue's rows (slice I9); created now so I9 needs no version bump.
 * - `pending`: the ONE camera hand-off request (key `current`), see {@link PendingMediaRequest}.
 *
 * Its own database, never `pdf-cache`: the PDF cache's LRU evicts by age and size and must never see a photo.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { createLogger } from '@/utils/logger';
import {
    ASSET_DB_NAME,
    QuotaError,
    hasRemoteCopy,
    isPendingLive,
    isQuotaExceeded,
    selectEvictions,
    type AssetPut,
    type AssetRecord,
    type AssetRow,
    type AssetStore,
    type AssetUsage,
    type PendingMediaRequest,
    type PendingUploads,
} from './AssetStore';

const log = createLogger('IdbAssetAdapter');
const MB = 1024 * 1024;
const PENDING_KEY = 'current';

/**
 * An upload queue row (spec 4.2; the queue is uploadQueue.ts). `local` -> put to staging -> `staged` -> promote ->
 * the row is dropped and the asset marked uploaded for the project. `parked` rows wait for a person: `failed`
 * (3 hash mismatches, or a refusal that means a bug) until a Retry tap, `forbidden` (no edit access) until the
 * role changes or the app starts again, `damaged` (the LOCAL bytes no longer hash to their name).
 */
export interface UploadRow {
    id: string;
    projectId: string;
    state: 'local' | 'staged' | 'parked';
    parked?: 'failed' | 'forbidden' | 'damaged';
    /** Transient failures in a row (network, 5xx, contention): the backoff exponent. */
    attempts: number;
    /** hash-mismatch answers so far (spec: at most 3 per row, then parked). */
    mismatches?: number;
    nextAt: number;
    lastError?: string;
}

interface AssetDB extends DBSchema {
    assets: { key: string; value: AssetRecord };
    uploads: { key: [string, string]; value: UploadRow };
    pending: { key: string; value: PendingMediaRequest };
}

export interface IdbAssetAdapterOptions {
    now?: () => number;
    /** navigator.storage.persist(), asked once, on the first put (spec 4.2: "requested on the first ingest"). */
    persist?: () => Promise<boolean>;
    persisted?: () => Promise<boolean>;
    estimate?: () => Promise<{ usage?: number; quota?: number }>;
}

const storage = (): StorageManager | undefined => (typeof navigator === 'undefined' ? undefined : navigator.storage);

export class IdbAssetAdapter implements AssetStore {
    private db: Promise<IDBPDatabase<AssetDB>> | null = null;
    private readonly now: () => number;
    private readonly persist: () => Promise<boolean>;
    private readonly persisted: () => Promise<boolean>;
    private readonly estimate: () => Promise<{ usage?: number; quota?: number }>;
    private persistAsked = false;
    private readonly urls = new Map<string, { url: string; holders: number }>();
    private readonly listeners = new Set<(p: PendingUploads) => void>();

    constructor(opts: IdbAssetAdapterOptions = {}) {
        this.now = opts.now ?? (() => Date.now());
        this.persist = opts.persist ?? (async () => (await storage()?.persist?.()) ?? false);
        this.persisted = opts.persisted ?? (async () => (await storage()?.persisted?.()) ?? false);
        this.estimate = opts.estimate ?? (async () => (await storage()?.estimate?.()) ?? {});
    }

    private open(): Promise<IDBPDatabase<AssetDB>> {
        this.db ??= openDB<AssetDB>(ASSET_DB_NAME, 1, {
            upgrade(db) {
                db.createObjectStore('assets', { keyPath: 'id' });
                db.createObjectStore('uploads', { keyPath: ['id', 'projectId'] });
                db.createObjectStore('pending');
            },
            // A newer tab wants to upgrade the schema: let it, and reopen on the next call.
            blocking: () => this.close(),
        });
        return this.db;
    }

    /** Close the connection (tests; a newer tab's upgrade). The next call reopens. */
    close(): void {
        const db = this.db;
        this.db = null;
        void db?.then((d) => d.close(), () => undefined);
    }

    async put(rec: AssetPut): Promise<void> {
        const db = await this.open();
        const now = this.now();
        // strict: resolved means on disk (M1), not merely handed to the OS.
        const tx = db.transaction('assets', 'readwrite', { durability: 'strict' });
        try {
            const write = (async () => {
                const prev = await tx.store.get(rec.id);
                await tx.store.put({
                    id: rec.id, mime: rec.mime, w: rec.w, h: rec.h, bytes: rec.bytes, blob: rec.blob,
                    createdAt: prev?.createdAt ?? now,
                    lastUsedAt: now,
                    uploaded: prev?.uploaded ?? {},
                });
            })();
            await Promise.all([write, tx.done]);
        } catch (e) {
            // A full origin usually aborts the transaction: the request then rejects with AbortError and only
            // the transaction carries the QuotaExceededError, so both are read.
            if (isQuotaExceeded(e) || isQuotaExceeded(tx.error)) throw new QuotaError(rec.bytes, tx.error ?? e);
            throw e;
        }
        if (!this.persistAsked) {
            this.persistAsked = true;
            await this.persist().catch((e: unknown) => log.warn('storage.persist() failed', e));
        }
        this.changed();
    }

    async has(id: string): Promise<boolean> {
        // A hit is a reuse (Ingest dedupes on it): it counts as a use for the LRU.
        return (await this.touch(id)) !== undefined;
    }

    async get(id: string, _ctx: { projectId?: string }): Promise<Blob> {
        const row = await this.touch(id);
        if (!row) throw new Error(`Asset ${id.slice(0, 12)} is not on this device`);
        return row.blob;
    }

    async url(id: string): Promise<string> {
        const held = this.urls.get(id);
        if (held) {
            held.holders++;
            return held.url;
        }
        const blob = await this.get(id, {});
        const again = this.urls.get(id);   // another caller won the race while we read
        if (again) {
            again.holders++;
            return again.url;
        }
        const url = URL.createObjectURL(blob);
        this.urls.set(id, { url, holders: 1 });
        return url;
    }

    release(id: string): void {
        const held = this.urls.get(id);
        if (!held) return;
        if (--held.holders > 0) return;
        this.urls.delete(id);
        URL.revokeObjectURL(held.url);
    }

    pending(listener: (p: PendingUploads) => void): () => void {
        this.listeners.add(listener);
        void this.localOnly().then((count) => {
            if (this.listeners.has(listener)) listener({ count });
        }, (e: unknown) => log.warn('pending count failed', e));
        return () => {
            this.listeners.delete(listener);
        };
    }

    async usage(): Promise<AssetUsage> {
        const rows = await this.rows();
        const [persisted, est] = await Promise.all([
            this.persisted().catch(() => false),
            this.estimate().catch(() => ({} as { usage?: number; quota?: number })),
        ]);
        return {
            assetsMb: rows.reduce((s, r) => s + r.bytes, 0) / MB,
            usedMb: (est.usage ?? 0) / MB,
            quotaMb: (est.quota ?? 0) / MB,
            persisted,
            localOnly: rows.filter((r) => !hasRemoteCopy(r)).length,
        };
    }

    async evictUploaded(capMb: number): Promise<{ deleted: number }> {
        const db = await this.open();
        const tx = db.transaction('assets', 'readwrite');
        const rows = await tx.store.getAll();
        // Selected and deleted in ONE transaction: no row can lose its remote copy in between (M1).
        const ids = selectEvictions(rows, capMb * MB);
        for (const id of ids) await tx.store.delete(id);
        await tx.done;
        if (ids.length) {
            log.info(`Evicted ${ids.length} uploaded asset(s)`);
            this.changed();
        }
        return { deleted: ids.length };
    }

    // ---- the upload queue's rows (uploadQueue.ts)

    async uploadRows(): Promise<UploadRow[]> {
        return (await this.open()).getAll('uploads');
    }

    async putUploadRow(row: UploadRow): Promise<void> {
        await (await this.open()).put('uploads', row);
        this.changed();
    }

    async deleteUploadRow(id: string, projectId: string): Promise<void> {
        await (await this.open()).delete('uploads', [id, projectId]);
        this.changed();
    }

    /** The local bytes without stamping the LRU clock (the queue reads them to stage); null when not here. */
    async localBlob(id: string): Promise<Blob | null> {
        return (await (await this.open()).get('assets', id))?.blob ?? null;
    }

    /**
     * Store bytes DOWNLOADED from a project (a collaborator's photo, already sha256-verified by the caller): they
     * have a remote copy, so the row is evictable from the start (M1 is about bytes that never left a device).
     */
    async putDownloaded(rec: AssetPut, projectId: string): Promise<void> {
        const db = await this.open();
        const now = this.now();
        const tx = db.transaction('assets', 'readwrite');
        const prev = await tx.store.get(rec.id);
        await tx.store.put({
            id: rec.id, mime: rec.mime, w: rec.w, h: rec.h, bytes: rec.bytes, blob: rec.blob,
            createdAt: prev?.createdAt ?? now,
            lastUsedAt: now,
            uploaded: { ...(prev?.uploaded ?? {}), [projectId]: true },
        });
        await tx.done;
        this.changed();
    }

    /** Record a promoted remote copy (the I9 upload queue's last step). */
    async markUploaded(id: string, projectId: string): Promise<void> {
        const db = await this.open();
        const tx = db.transaction('assets', 'readwrite');
        const row = await tx.store.get(id);
        if (row) await tx.store.put({ ...row, uploaded: { ...row.uploaded, [projectId]: true } });
        await tx.done;
        this.changed();
    }

    async savePending(req: PendingMediaRequest): Promise<void> {
        const db = await this.open();
        await db.put('pending', req, PENDING_KEY);
    }

    async takePending(): Promise<PendingMediaRequest | null> {
        const db = await this.open();
        const tx = db.transaction('pending', 'readwrite');
        const req = await tx.store.get(PENDING_KEY);
        // Taken = answered: a live one goes to the caller, a stale one is dropped. Either way it is gone.
        if (req) await tx.store.delete(PENDING_KEY);
        await tx.done;
        return req && isPendingLive(req, this.now()) ? req : null;
    }

    /** One asset's row without its bytes (tests, diagnostics). */
    async row(id: string): Promise<AssetRow | undefined> {
        const rec = await (await this.open()).get('assets', id);
        if (!rec) return undefined;
        const { blob: _blob, ...row } = rec;
        return row;
    }

    private async rows(): Promise<AssetRecord[]> {
        return (await this.open()).getAll('assets');
    }

    private async localOnly(): Promise<number> {
        return (await this.rows()).filter((r) => !hasRemoteCopy(r)).length;
    }

    /** Read a row and stamp lastUsedAt (the LRU clock). */
    private async touch(id: string): Promise<AssetRecord | undefined> {
        const db = await this.open();
        const tx = db.transaction('assets', 'readwrite');
        const row = await tx.store.get(id);
        if (row) await tx.store.put({ ...row, lastUsedAt: this.now() });
        await tx.done;
        return row;
    }

    private changed(): void {
        if (this.listeners.size === 0) return;
        void this.localOnly().then((count) => {
            for (const l of this.listeners) l({ count });
        }, (e: unknown) => log.warn('pending count failed', e));
    }
}

/** The app's local AssetStore. */
export const assetStore = new IdbAssetAdapter();
